import { CLIENT_ROLES, OWNER_ROLES } from '@/constants/roles'
import { hasRoleFromGroup } from '@/utils/auth/roleGroups'
import { isBuyerUser } from '@/utils/auth/userRoles'

export type AccessSubject = {
  roles: string[] | null | undefined
  tokenRoles: string[]
}

const hasRole = ({ tokenRoles }: AccessSubject, name: string) =>
  tokenRoles.includes(name)

export const isSeniorManager = ({ tokenRoles }: AccessSubject) =>
  hasRoleFromGroup(tokenRoles, 'SENIOR_MANAGERS')

export const isCatalogEditor = ({ tokenRoles }: AccessSubject) =>
  hasRoleFromGroup(tokenRoles, 'CATALOG_EDITORS')

export const isApprover = ({ tokenRoles }: AccessSubject) =>
  hasRoleFromGroup(tokenRoles, 'CATALOG_APPROVERS')

export const getExchangesAccess = (subject: AccessSubject) => ({
  canManagePartnerships: isSeniorManager(subject),
  canManageStores: hasRoleFromGroup(subject.tokenRoles, 'STORE_MANAGERS'),
  isStockManager: hasRole(subject, OWNER_ROLES.STOCK_MANAGER),
  isProductSupplierUser: hasRole(subject, CLIENT_ROLES.PRODUCT_SUPPLIER_USER),
})

export const canSeeObjects = (subject: AccessSubject) =>
  hasRole(subject, OWNER_ROLES.ADMIN) || isBuyerUser(subject.tokenRoles)

const canSeeExchanges = (subject: AccessSubject) => {
  const access = getExchangesAccess(subject)
  return (
    access.canManagePartnerships ||
    access.canManageStores ||
    access.isProductSupplierUser
  )
}

export const hasNomenclatureEditorRole = (subject: AccessSubject) =>
  hasRole(subject, OWNER_ROLES.NOMENCLATURE_EDITOR)

export const isNomenclatureEditorOnly = (subject: AccessSubject) =>
  hasNomenclatureEditorRole(subject) && subject.roles?.length === 1

const ROUTE_RULES: Array<{
  prefix: string
  isAllowed: (subject: AccessSubject) => boolean
}> = [
  { prefix: '/exchanges', isAllowed: canSeeExchanges },
  { prefix: '/clients', isAllowed: isSeniorManager },
  { prefix: '/construction-sites', isAllowed: canSeeObjects },
  { prefix: '/catalog-approval', isAllowed: isApprover },
  { prefix: '/categories-catalog', isAllowed: isCatalogEditor },
  { prefix: '/nomenclature-groups-catalog', isAllowed: isCatalogEditor },
  { prefix: '/characteristics-catalog', isAllowed: isCatalogEditor },
  { prefix: '/nomenclature-feed', isAllowed: isCatalogEditor },
  { prefix: '/attribute-processing', isAllowed: isCatalogEditor },
  { prefix: '/nomenclature-import', isAllowed: isCatalogEditor },
  { prefix: '/system/priority-config', isAllowed: isCatalogEditor },
]

const matchesPrefix = (pathname: string, prefix: string) =>
  pathname === prefix || pathname.startsWith(`${prefix}/`)

export const canAccessRoute = (pathname: string, subject: AccessSubject) => {
  const rule = ROUTE_RULES.find(({ prefix }) => matchesPrefix(pathname, prefix))
  return rule ? rule.isAllowed(subject) : true
}
